import {
    Backdrop,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    Grid,
    MenuItem,
    Select,
    TextField,
    Typography
} from '@mui/material';
import { ChangeEvent, useMemo, useState } from 'react';
import { useGet } from '../../lib/api/hooks';
import { ConfigurableItemDto } from '../ListItem';
import ConditionedSnackbar from '../ConditionedSnackbar';
import { AncillaryHourRequestObject } from '../../lib/models/AncillaryHourModel';
import { saveButtonStyle } from '../../style';
import { post } from '../../lib/api';
import moment from 'moment';
import theme from '../../theme';
import { useScreenSmallerThan } from '../../lib/hooks/breakpoint-hooks';
import InternalDatePicker from '../Utils/InternalDatePicker';
import { toast } from 'react-toastify';

const INVALID_DATE = 'Invalid date';

type AddAncillaryHoursProps = {
    cycleInterval: string;
    refreshPage: () => void;
    refreshHeaderData: () => void;
};

const AddAncillaryHours = ({
    refreshPage,
    refreshHeaderData,
    cycleInterval
}: AddAncillaryHoursProps) => {
    const isSmallerThanLarge = useScreenSmallerThan('lg');
    const intervalName = encodeURIComponent('How long did it take?');
    const activityName = encodeURIComponent('What did you work on?');

    const { data: intervalData, loading: intervalLoading } = useGet<ConfigurableItemDto[]>(
        `appconfigs/by-name/${intervalName}`
    );
    const { data: activityData, loading: activityLoading } = useGet<ConfigurableItemDto[]>(
        `appconfigs/by-name/${activityName}`
    );

    const [activityValue, setActivityValue] = useState('');
    const [activityValueError, setActivityValueError] = useState(false);
    const [minutesTakenValue, setMinutesTakenValue] = useState('');
    const [minutesTakenValueError, setMinutesTakenValueError] = useState(false);
    const [dateValue, onDateChange] = useState<string>(moment().startOf('date').toString());
    const [notes, setNotes] = useState('');

    const [isErrored, setErrored] = useState(false);
    const [isDialogOpened, setDialogOpened] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const minDate = useMemo(() => moment().subtract(30, 'days').startOf('date'), []);
    const maxDate = useMemo(() => new Date(cycleInterval.split('-')[1].trim()), [cycleInterval]);

    const handleCloseDialog = () => {
        setDialogOpened(false);
        setActivityValue('');
        setActivityValueError(false);
        setMinutesTakenValue('');
        setMinutesTakenValueError(false);
        onDateChange(moment().startOf('date').toString());
        setNotes('');
    };

    const handleSave = async (ev: ChangeEvent<unknown>) => {
        ev.preventDefault();
        if (activityValue === '') setActivityValueError(true);
        if (minutesTakenValue === '') setMinutesTakenValueError(true);

        const refDate = new Date(dateValue);
        const date = new Date(refDate.getTime());
        const dateOnly = moment(refDate).startOf('date').toDate();
        if (minutesTakenValue !== '' && activityValue !== '' && dateValue !== INVALID_DATE) {
            const ancillaryHour: AncillaryHourRequestObject = {
                date: date,
                exactDate: date.toLocaleDateString("en-US"),
                activity: activityValue,
                notes: notes,
                minutesTaken: +minutesTakenValue,
                createdAt: moment().utc().toDate()
            };

            if (dateOnly < minDate.toDate() || dateOnly > maxDate) {
                toast.error('The provided date is not in the range', {
                    toastId: 'providedDateFail'
                });
                return;
            }

            onNewItemAdd(ancillaryHour);
            setDialogOpened(false);
        }
    };

    const onNewItemAdd = async (object: AncillaryHourRequestObject | null) => {
        try {
            setErrored(false);
            setIsLoading(true);
            const result = await post(`ancillary-hours/submit`, object);
            result.data == null ? setErrored(true) : setErrored(false);
            setSnackbarArgument('add-item');
            setShowSnackbar(true);
            refreshPage();
            refreshHeaderData();
            handleCloseDialog();
        } catch (error) {
            setErrored(true);
            setSnackbarArgument('add-item');
            setShowSnackbar(true);
        } finally {
            setIsLoading(false);
        }
    };

    //Snackbar
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarArgument, setSnackbarArgument] = useState<string>('');
    const snackBarMessage = (functionName: string) => {
        switch (functionName) {
            case 'add-item':
                return `Item ${isErrored ? 'was not' : ''} succesfully added.`;
            default:
                return '';
        }
    };
    //endregion

    return (
        <>
            {!intervalLoading && intervalData && !activityLoading && activityData ? (
                <>
                    <Button
                        sx={{
                            borderRadius: '0.5rem',
                            border: 1,
                            weight: '700',
                            fontSize: '0.875rem',
                            height: '2.375rem',
                            width: '10rem',
                            color: '#979797',
                            paddingY: '0.625rem'
                        }}
                        onClick={() => setDialogOpened(true)}>
                        Add Ancillary Hours
                    </Button>

                    <Dialog open={isDialogOpened} onClose={handleCloseDialog}>
                        <DialogTitle variant="h1" sx={{ paddingLeft: '0.9375rem' }}>
                            Add Ancillary Time
                        </DialogTitle>
                        <DialogContent
                            style={{
                                paddingLeft: '0.9375rem',
                                paddingRight: '0.9375rem',
                                width: !isSmallerThanLarge ? '400px' : '280px'
                            }}>
                            <form>
                                <Grid container direction="column">
                                    <Grid item xs={12}>
                                        <FormControl
                                            sx={{ minWidth: 120 }}
                                            size="small"
                                            error={activityValueError}
                                            fullWidth>
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    color: theme.palette.cardSubtitle.main,
                                                    fontWeight: 500
                                                }}>
                                                What did you work on?
                                            </Typography>
                                            <Select
                                                margin="dense"
                                                variant="outlined"
                                                type="number"
                                                value={activityValue}
                                                onChange={(v) => {
                                                    setActivityValueError(false);
                                                    setActivityValue(v.target.value);
                                                }}
                                                sx={{
                                                    maxWidth: !isSmallerThanLarge
                                                        ? '370px'
                                                        : '250px',
                                                    '& .MuiInputBase-root': {
                                                        height: 40
                                                    }
                                                }}
                                                inputProps={{ 'aria-label': 'Without label' }}>
                                                {activityData?.map((item, index) => (
                                                    <MenuItem key={index} value={item.value}>
                                                        {item.value}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {activityValueError ? (
                                                <FormHelperText error={activityValueError}>
                                                    Please select an option.
                                                </FormHelperText>
                                            ) : null}
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControl
                                            sx={{ minWidth: 120 }}
                                            size="small"
                                            error={minutesTakenValueError}
                                            fullWidth>
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    color: theme.palette.cardSubtitle.main,
                                                    fontWeight: 500
                                                }}>
                                                How long did it take?
                                            </Typography>
                                            <Select
                                                margin="dense"
                                                variant="outlined"
                                                type="text"
                                                fullWidth
                                                value={minutesTakenValue}
                                                onChange={(v) => {
                                                    setMinutesTakenValueError(false);
                                                    setMinutesTakenValue(v.target.value);
                                                }}
                                                sx={{
                                                    '& .MuiInputBase-root': {
                                                        height: 40
                                                    }
                                                }}>
                                                {intervalData
                                                    ?.sort(
                                                        (a, b) => Number(a.value) - Number(b.value)
                                                    )
                                                    .map((item, index) => (
                                                        <MenuItem key={index} value={item.value}>
                                                            {item.value} minutes
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                            {minutesTakenValueError ? (
                                                <FormHelperText error={minutesTakenValueError}>
                                                    Please select an option.
                                                </FormHelperText>
                                            ) : null}
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl sx={{ minWidth: 120 }} size="small" fullWidth>
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    color: theme.palette.cardSubtitle.main,
                                                    fontWeight: 500
                                                }}>
                                                What was the date?
                                            </Typography>
                                            <InternalDatePicker
                                                dateValue={dateValue}
                                                min={minDate.toString()}
                                                max={maxDate.toString()}
                                                onDateChange={(date: string) => onDateChange(date)}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl size="small" fullWidth>
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    color: theme.palette.cardSubtitle.main,
                                                    fontWeight: 500
                                                }}>
                                                Notes
                                            </Typography>
                                            <TextField
                                                id="ancillary-hours-notes"
                                                multiline
                                                minRows={2}
                                                placeholder="Optional"
                                                value={notes}
                                                onChange={(e) => setNotes(e.target.value)}
                                            />
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </form>
                        </DialogContent>
                        <DialogActions
                            style={{
                                justifyContent: 'space-between',
                                paddingLeft: '0.9375rem',
                                paddingBottom: '1.25rem',
                                paddingTop: '0.625rem'
                            }}>
                            <Button
                                disabled={isLoading}
                                onClick={handleSave}
                                sx={saveButtonStyle}
                                type="submit">
                                Submit Time
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            ) : (
                <Backdrop
                    sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={activityLoading && intervalLoading}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}
            <ConditionedSnackbar
                open={showSnackbar}
                handleClose={() => setShowSnackbar(false)}
                severity={!isErrored ? 'success' : 'error'}
                message={snackBarMessage(snackbarArgument)}
            />
        </>
    );
};

export default AddAncillaryHours;
